import { Link, useRouterState } from "@tanstack/react-router";
import { BookOpenCheck, BriefcaseBusiness, CalendarDays, CalendarPlus, ClipboardList, CreditCard, Gauge, Home, Search, Sparkles } from "lucide-react";
import { workspaceForPath } from "@/lib/workspace-view";

const CUSTOMER_TABS = [
  { to: "/customer", label: "Home", icon: Home, exact: true },
  { to: "/book", label: "Book", icon: CalendarPlus, exact: false },
  { to: "/bookings", label: "Activity", icon: ClipboardList, exact: false },
  { to: "/home", label: "My Home", icon: Home, exact: false },
  { to: "/customer/ai", label: "AI", icon: Sparkles, exact: false },
] as const;

const PARTNER_TABS = [
  { to: "/partner", label: "Business", icon: BriefcaseBusiness, exact: true },
  { to: "/partner/offers", label: "Opportunities", shortLabel: "Find work", icon: Search, exact: false },
  { to: "/partner/jobs", label: "Jobs", icon: ClipboardList, exact: false },
  { to: "/partner/business/calendar", label: "Calendar", icon: CalendarDays, exact: false },
  { to: "/partner/payments", label: "Money", icon: CreditCard, exact: false },
] as const;

const OWNER_TABS = [
  { to: "/owner", label: "Home", icon: Home, exact: true },
  { to: "/owner/approvals", label: "Approvals", icon: BookOpenCheck, exact: false },
  { to: "/owner/automation", label: "Control", icon: Gauge, exact: false },
  { to: "/owner/memberships", label: "Plans", icon: CreditCard, exact: false },
  { to: "/owner/achievements", label: "Audit", icon: ClipboardList, exact: false },
] as const;

function tabIsActive(
  pathname: string,
  tab: { to: string; exact: boolean; label?: string },
  workspace: "customer" | "partner" | "owner",
) {
  if (workspace === "partner") {
    if (tab.label === "Business") {
      return pathname === "/partner"
        || (pathname.startsWith("/partner/business") && !pathname.startsWith("/partner/business/calendar"));
    }
    if (tab.label === "Opportunities") return pathname.startsWith("/partner/offers");
    if (tab.label === "Jobs") return pathname.startsWith("/partner/jobs");
    if (tab.label === "Calendar") return pathname.startsWith("/partner/business/calendar");
    if (tab.label === "Money") return pathname.startsWith("/partner/payments") || pathname.startsWith("/partner/wallet");
  }
  if (tab.exact) return pathname === tab.to;
  return pathname.startsWith(tab.to);
}

export function BottomNav({ workspace: workspaceOverride }: { workspace?: "customer" | "partner" | "owner" } = {}) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const workspace = workspaceOverride ?? workspaceForPath(pathname);
  const tabs = workspace === "partner" ? PARTNER_TABS : workspace === "owner" ? OWNER_TABS : CUSTOMER_TABS;
  return (
    <nav aria-label={workspace + " app navigation"} className="bottom-nav fixed inset-x-0 bottom-0 z-40 border-t border-border/60 bg-card/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl">
      <div className={"mx-auto flex h-16 items-center justify-around px-1 "+(workspace==="partner"?"max-w-xl":"max-w-lg")}>
        {tabs.map((tab) => {
          const active = tabIsActive(pathname, tab, workspace);
          return (
            <Link key={tab.to} to={tab.to} aria-label={tab.label} aria-current={active ? "page" : undefined} className={active ? "bottom-nav-item text-primary" : "bottom-nav-item text-muted-foreground"}>
              <tab.icon className="h-5 w-5" aria-hidden />
              <span className="truncate text-[10px] font-semibold leading-tight">{"shortLabel" in tab ? tab.shortLabel : tab.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
